import { useState } from "react";
import { Search, ArrowUpDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EmptyState } from "./EmptyState";

/**
 * Reusable data table with search, multiple filters and optional sorting.
 * columns: [{ key, header, render?, className? }]
 * filters: [{ key, label, options:[{value,label}] }]  (also accepts singular `filter`)
 * sorts: [{ value, label, fn(a,b) }]
 */
export const DataTable = ({
  columns,
  rows = [],
  searchKeys = [],
  searchPlaceholder = "Search…",
  filter,
  filters,
  sorts,
  emptyIcon,
  emptyImage,
  emptyTitle = "Nothing here yet",
  emptyDescription = "Records added to this table will appear here.",
  emptyActionLabel,
  onEmptyAction,
  onRowClick,
  testid,
}) => {
  const [query, setQuery] = useState("");
  const activeFilters = filters || (filter ? [filter] : []);
  const [filterValues, setFilterValues] = useState({});
  const [sortValue, setSortValue] = useState("");

  let data = [...rows];
  if (query && searchKeys.length) {
    const q = query.toLowerCase();
    data = data.filter((r) =>
      searchKeys.some((k) => String(r[k] ?? "").toLowerCase().includes(q))
    );
  }
  activeFilters.forEach((f) => {
    const val = filterValues[f.key];
    if (val && val !== "all") data = data.filter((r) => r[f.key] === val);
  });
  if (sortValue && sorts) {
    const s = sorts.find((x) => x.value === sortValue);
    if (s?.fn) data.sort(s.fn);
  }

  return (
    <div data-testid={testid || "data-table"} className="space-y-4">
      {(searchKeys.length > 0 || activeFilters.length > 0 || sorts) && (
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          {searchKeys.length > 0 && (
            <div className="relative flex-1">
              <Search
                size={16}
                strokeWidth={2}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#5A6B8C]"
              />
              <Input
                data-testid="data-table-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="rounded-full border-[#E4E9F3] bg-white pl-9 focus-visible:ring-[var(--accent-primary)]/40"
              />
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {activeFilters.map((f) => (
              <Select
                key={f.key}
                value={filterValues[f.key] || "all"}
                onValueChange={(v) =>
                  setFilterValues((prev) => ({ ...prev, [f.key]: v }))
                }
              >
                <SelectTrigger
                  data-testid={`data-table-filter-${f.key}`}
                  className="w-auto min-w-[130px] rounded-full border-[#E4E9F3] bg-white"
                >
                  <SelectValue placeholder={f.label} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{f.label}: All</SelectItem>
                  {f.options.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ))}
            {sorts && (
              <Select value={sortValue} onValueChange={setSortValue}>
                <SelectTrigger
                  data-testid="data-table-sort"
                  className="w-auto min-w-[150px] rounded-full border-[#E4E9F3] bg-white"
                >
                  <ArrowUpDown size={13} className="mr-1 opacity-50" />
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  {sorts.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      )}

      {data.length === 0 ? (
        <EmptyState
          icon={emptyIcon}
          image={rows.length === 0 ? emptyImage : undefined}
          title={emptyTitle}
          description={emptyDescription}
          actionLabel={emptyActionLabel}
          onAction={onEmptyAction}
        />
      ) : (
        <div className="w-full overflow-x-auto rounded-2xl border border-[#E4E9F3] bg-white">
          <table className="w-full min-w-[640px] border-collapse text-left">
            <thead>
              <tr className="bg-[#F4F6FB]">
                {columns.map((c) => (
                  <th
                    key={c.key}
                    className={`border-b border-[#E4E9F3] px-4 py-3 text-xs font-bold uppercase tracking-[0.1em] text-[#5A6B8C] ${c.thClassName || ""}`}
                  >
                    {c.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr
                  key={row.id ?? i}
                  data-testid={`data-table-row-${i}`}
                  role={onRowClick ? "button" : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  aria-label={onRowClick && row.title ? `View ${row.title}` : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onKeyDown={onRowClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onRowClick(row); } } : undefined}
                  className={`border-b border-[#EEF2FB] align-top transition-colors last:border-0 hover:bg-[#F7F9FE] ${onRowClick ? "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E0A82E]/50" : ""}`}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={`px-4 py-3.5 text-sm text-[#14245F] ${c.className || ""}`}
                    >
                      {c.render ? c.render(row) : row[c.key] || "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
